// SPIKE: Granola connect + MCP roundtrip test page. Will be replaced with the
// generic connector settings UI once the framework is in place.

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { startGranolaConnect, testGranolaConnection } from "../lib/api";
import { Crumbs } from "../components/Crumbs";
import { PageTitle } from "../components/PageTitle";

type TestState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "ok"; payload: unknown }
  | { kind: "error"; message: string };

export function ConnectGranola() {
  const [searchParams] = useSearchParams();
  const justConnected = searchParams.get("ok") === "true";
  const errorParam = searchParams.get("error");

  const [connectPending, setConnectPending] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [test, setTest] = useState<TestState>({ kind: "idle" });

  // If we just bounced back from the OAuth callback, kick off the test
  // automatically so David sees the full roundtrip without an extra click.
  useEffect(() => {
    if (justConnected && test.kind === "idle") {
      void runTest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justConnected]);

  async function onConnect() {
    setConnectError(null);
    setConnectPending(true);
    try {
      const { authorizeUrl } = await startGranolaConnect();
      window.location.href = authorizeUrl;
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : "Could not start connect.");
      setConnectPending(false);
    }
  }

  async function runTest() {
    setTest({ kind: "running" });
    try {
      const payload = await testGranolaConnection();
      setTest({ kind: "ok", payload });
    } catch (err) {
      setTest({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <Crumbs trail={[{ label: "Settings", to: "/settings" }, "Granola"]} />
      <PageTitle lede="Connect Granola once. Klemento will read transcripts on demand. Your data stays in Granola; Klemento only fetches what you ask it to coach.">
        Connect Granola.
      </PageTitle>

      {errorParam ? (
        <ErrorBox message={`Granola callback returned an error: ${errorParam}`} />
      ) : null}
      {justConnected ? (
        <div
          role="status"
          style={{
            background: "rgb(var(--color-success, 30 130 80) / 0.08)",
            border: "1px solid rgb(var(--color-success, 30 130 80) / 0.3)",
            color: "rgb(var(--color-ink))",
            padding: "10px 14px",
            borderRadius: 10,
            marginBottom: 14,
            fontSize: 14,
          }}
        >
          Granola tokens stored. Running MCP roundtrip test below.
        </div>
      ) : null}
      {connectError ? <ErrorBox message={connectError} /> : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {justConnected ? (
          <Link to="/connectors/granola/meetings" className="btn btn-primary" style={{ width: "100%", textDecoration: "none" }}>
            Browse your Granola meetings &rarr;
          </Link>
        ) : (
          <button type="button" onClick={onConnect} disabled={connectPending} className="btn btn-primary" style={{ width: "100%" }}>
            {connectPending ? "Redirecting to Granola..." : "Connect Granola"}
          </button>
        )}

        {justConnected ? (
          <button type="button" onClick={onConnect} disabled={connectPending} className="btn btn-secondary" style={{ width: "100%" }}>
            {connectPending ? "Redirecting to Granola..." : "Re-connect Granola"}
          </button>
        ) : null}

        <button
          type="button"
          onClick={runTest}
          disabled={test.kind === "running"}
          className="btn btn-secondary"
          style={{ width: "100%" }}
        >
          {test.kind === "running" ? "Running MCP roundtrip..." : "Run MCP roundtrip test"}
        </button>
      </div>

      {test.kind === "ok" ? (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: "0 0 8px" }}>MCP roundtrip OK</h2>
          <pre
            style={{
              background: "rgb(var(--color-surface-strong))",
              padding: 16,
              borderRadius: 10,
              fontSize: 12,
              overflow: "auto",
              maxHeight: 480,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {JSON.stringify(test.payload, null, 2)}
          </pre>
        </div>
      ) : null}

      {test.kind === "error" ? (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: "0 0 8px", color: "rgb(var(--color-warning))" }}>
            MCP roundtrip failed
          </h2>
          <pre
            style={{
              background: "rgb(var(--color-warning) / 0.06)",
              border: "1px solid rgb(var(--color-warning) / 0.3)",
              padding: 16,
              borderRadius: 10,
              fontSize: 13,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {test.message}
          </pre>
        </div>
      ) : null}
    </div>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        background: "rgb(var(--color-warning) / 0.08)",
        border: "1px solid rgb(var(--color-warning) / 0.3)",
        color: "rgb(var(--color-warning))",
        padding: "10px 14px",
        borderRadius: 10,
        marginBottom: 14,
        fontSize: 14,
      }}
    >
      {message}
    </div>
  );
}
